import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Plan, Region } from '@court-sync/shared';
import {
  subscriptionService,
  type SubscriptionResponse,
} from '../services/subscription';
import { STALE_TIME, queryClient } from '../queryClient';
import { queryKeys } from './keys';

export async function fetchSubscriptionQuery(options?: { force?: boolean }): Promise<SubscriptionResponse | null> {
  const key = queryKeys.subscription.current();
  if (options?.force) {
    await queryClient.invalidateQueries({ queryKey: key });
  }
  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: () => subscriptionService.getCurrentSubscription(),
    staleTime: STALE_TIME.MEDIUM,
  });
}

export async function fetchPlansQuery(
  region?: Region,
  options?: { force?: boolean; productLine?: 'academy' | 'club' },
): Promise<Plan[]> {
  const productLine = options?.productLine;
  const key = queryKeys.subscription.plans(region ?? null, productLine ?? null);
  if (options?.force) {
    await queryClient.invalidateQueries({ queryKey: key });
  }
  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: () => subscriptionService.getPlans(region, productLine),
    staleTime: STALE_TIME.MEDIUM,
  });
}

export function useSubscriptionQuery() {
  const queryClient = useQueryClient();

  const subscriptionQuery = useQuery({
    queryKey: queryKeys.subscription.current(),
    queryFn: () => subscriptionService.getCurrentSubscription(),
    staleTime: STALE_TIME.MEDIUM,
  });

  const { refetch } = subscriptionQuery;

  const refreshSubscription = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.subscription.current() });
    invalidateAcademyTierQueries();
    return refetch();
  }, [queryClient, refetch]);

  const refreshAfterPayment = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return refreshSubscription();
  }, [refreshSubscription]);

  return {
    ...subscriptionQuery,
    refreshSubscription,
    refreshAfterPayment,
  };
}

export function usePlansQuery(region?: Region, productLine: 'academy' | 'club' = 'academy') {
  return useQuery({
    queryKey: queryKeys.subscription.plans(region ?? null, productLine),
    queryFn: () => subscriptionService.getPlans(region, productLine),
    staleTime: STALE_TIME.MEDIUM,
  });
}

export function invalidateSubscriptionQueries(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.subscription.all });
}

/** Clears cached academy rows and owner Stripe tier used for feature gating after plan changes. */
export function invalidateAcademyTierQueries(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.academies.all });
}
